<template>
  <main class="showcase-page">
    <section class="showcase-hero">
      <div class="hero-orbit orbit-one" />
      <div class="hero-orbit orbit-two" />
      <div class="hero-copy">
        <p class="eyebrow">OPEN GALLERY</p>
        <h1>展示作品</h1>
        <p>让材料、技艺与想象被看见。这里不设赛程，只呈现作品本身。</p>
      </div>
      <button type="button" class="upload-entry" @click="goUpload">
        <span>上传作品</span>
        <a-icon type="arrow-right" />
      </button>
    </section>

    <section class="showcase-content">
      <div class="toolbar">
        <div class="categories">
          <button :class="{ active: !query.categoryName }" @click="selectCategory('')">全部</button>
          <button
            v-for="category in categories"
            :key="category"
            :class="{ active: query.categoryName === category }"
            @click="selectCategory(category)"
          >{{ category }}</button>
        </div>
        <a-input-search
          v-model.trim="query.keyword"
          class="work-search"
          placeholder="搜索作品、作者或材料"
          allow-clear
          @search="search"
        />
      </div>

      <div v-if="loading" class="state">正在整理展厅...</div>
      <div v-else-if="!works.length" class="state">暂时没有符合条件的作品</div>
      <div v-else class="work-grid">
        <article
          v-for="(work, index) in works"
          :key="work.id"
          class="work-card"
          @click="openDetail(work.id)"
        >
          <div class="image-frame">
            <img
              :src="thumbnail(work)"
              :alt="work.title"
              loading="lazy"
              decoding="async"
              :fetchpriority="index < 4 ? 'high' : 'low'"
              @error="handleImageError"
            >
            <span class="category-chip">{{ work.categoryName }}</span>
          </div>
          <div class="card-copy">
            <div>
              <h2>{{ work.title }}</h2>
              <p>{{ work.authorName }}</p>
            </div>
            <span class="view-count"><a-icon type="eye" /> {{ work.viewCount || 0 }}</span>
          </div>
          <p v-if="work.material" class="material">{{ work.material }}</p>
        </article>
      </div>

      <a-pagination
        v-if="query.total > query.limit"
        class="pagination"
        :current="query.page"
        :page-size="query.limit"
        :total="query.total"
        @change="changePage"
      />
    </section>
  </main>
</template>

<script>
import { mapGetters } from "vuex";
import { getShowcaseCategories, getShowcaseWorks } from "@/api/Showcase";
import { replaceBrokenImage, toWorkThumbnailUrl } from "@/utils/workImage";
import toLogin from "@/utils/login";

const FALLBACK_IMAGE = require("@/assets/images/nopic.png");

export default {
  name: "ShowcaseWorks",
  data() {
    return {
      loading: false,
      categories: [],
      works: [],
      query: { keyword: "", categoryName: "", page: 1, limit: 12, total: 0 },
    };
  },
  computed: mapGetters(["isLogin"]),
  mounted() {
    this.loadCategories();
    this.loadWorks();
  },
  methods: {
    loadCategories() {
      getShowcaseCategories().then((res) => {
        this.categories = Array.isArray(res.data) ? res.data : [];
      });
    },
    loadWorks() {
      this.loading = true;
      getShowcaseWorks({
        keyword: this.query.keyword,
        categoryName: this.query.categoryName,
        page: this.query.page,
        limit: this.query.limit,
      }).then((res) => {
        this.works = Array.isArray(res.data) ? res.data : [];
        this.query.total = Number(res.total || 0);
      }).catch((error) => {
        this.$message.error((error && error.msg) || "展示作品加载失败");
      }).finally(() => {
        this.loading = false;
      });
    },
    selectCategory(category) {
      this.query.categoryName = category;
      this.query.page = 1;
      this.loadWorks();
    },
    search() {
      this.query.page = 1;
      this.loadWorks();
    },
    changePage(page) {
      this.query.page = page;
      this.loadWorks();
      window.scrollTo({ top: 360, behavior: "smooth" });
    },
    thumbnail(work) {
      return toWorkThumbnailUrl(work.coverUrl || (work.imageList || [])[0]) || FALLBACK_IMAGE;
    },
    handleImageError(event) {
      replaceBrokenImage(event, FALLBACK_IMAGE);
    },
    openDetail(id) {
      this.$router.push({ name: "ShowcaseWorkDetail", params: { id } });
    },
    goUpload() {
      if (!this.isLogin) {
        toLogin(true, "/ShowcaseWorksUpload");
        return;
      }
      this.$router.push({ name: "ShowcaseWorksUpload" });
    },
  },
};
</script>

<style lang="scss" scoped>
.showcase-page { min-height: calc(100vh - 180px); background: #f3efe7; color: #25221d; }
.showcase-hero { position: relative; min-height: 330px; overflow: hidden; padding: 72px max(6vw, 36px); background: #173d34; color: #f7f0df; }
.hero-copy { position: relative; z-index: 2; max-width: 720px; }
.eyebrow { margin: 0 0 12px; color: #e6b566; font-size: 13px; letter-spacing: .28em; }
.hero-copy h1 { margin: 0; font-family: Georgia, "Noto Serif SC", serif; font-size: clamp(48px, 6vw, 82px); font-weight: 500; }
.hero-copy > p:last-child { margin-top: 18px; color: rgba(247,240,223,.72); font-size: 17px; letter-spacing: .06em; }
.hero-orbit { position: absolute; border: 1px solid rgba(230,181,102,.28); border-radius: 50%; }
.orbit-one { width: 420px; height: 420px; right: 5%; top: -210px; }
.orbit-two { width: 240px; height: 240px; right: 17%; bottom: -150px; }
.upload-entry { position: absolute; z-index: 2; right: max(6vw, 36px); bottom: 52px; display: flex; gap: 30px; align-items: center; border: 0; border-radius: 999px; padding: 17px 24px; background: #e6b566; color: #173d34; font-weight: 700; cursor: pointer; }
.showcase-content { width: min(1320px, calc(100% - 48px)); margin: 0 auto; padding: 42px 0 72px; }
.toolbar { display: flex; justify-content: space-between; gap: 24px; align-items: center; margin-bottom: 28px; }
.categories { display: flex; flex-wrap: wrap; gap: 10px; }
.categories button { border: 1px solid #cabfae; border-radius: 999px; padding: 9px 18px; background: transparent; color: #665f55; cursor: pointer; }
.categories button.active { border-color: #173d34; background: #173d34; color: #fff; }
.work-search { width: 300px; flex: 0 0 auto; }
.work-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 22px; }
.work-card { overflow: hidden; border-radius: 4px 4px 18px 18px; background: #fffdf9; box-shadow: 0 12px 35px rgba(65,53,35,.07); cursor: pointer; transition: transform .25s ease, box-shadow .25s ease; }
.work-card:hover { transform: translateY(-5px); box-shadow: 0 18px 45px rgba(65,53,35,.14); }
.image-frame { position: relative; aspect-ratio: 4 / 3; overflow: hidden; background: #ded8cd; }
.image-frame img { width: 100%; height: 100%; object-fit: cover; transition: transform .5s ease; }
.work-card:hover img { transform: scale(1.035); }
.category-chip { position: absolute; left: 14px; top: 14px; border-radius: 999px; padding: 5px 11px; background: rgba(23,61,52,.9); color: #fff; font-size: 12px; }
.card-copy { display: flex; justify-content: space-between; gap: 12px; padding: 18px 18px 5px; }
.card-copy h2 { margin: 0; font-family: Georgia, "Noto Serif SC", serif; font-size: 20px; line-height: 1.4; }
.card-copy p { margin: 7px 0 0; color: #81786c; }
.view-count { color: #9b9081; white-space: nowrap; }
.material { margin: 0; padding: 4px 18px 20px; color: #aa7a38; font-size: 13px; }
.state { padding: 100px 0; text-align: center; color: #867d70; }
.pagination { margin-top: 42px; text-align: center; }
@media (max-width: 960px) { .work-grid { grid-template-columns: repeat(2, 1fr); } .toolbar { align-items: stretch; flex-direction: column; } .work-search { width: 100%; } }
@media (max-width: 600px) { .showcase-hero { padding: 48px 22px 110px; } .upload-entry { left: 22px; right: auto; bottom: 34px; } .showcase-content { width: calc(100% - 28px); } .work-grid { grid-template-columns: 1fr; } }
</style>
